import dagre from 'dagre';
import type { Edge, Node } from 'reactflow';

/**
 * Utility function to automatically layout nodes using Dagre
 * @param nodes - Array of ReactFlow nodes
 * @param edges - Array of ReactFlow edges
 * @param direction - Layout direction (TB = top-bottom, LR = left-right)
 * @returns Array of nodes with updated positions
 */
export function getLayoutedElements(nodes: Node[], edges: Edge[], direction = 'TB'): Node[] {
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));
	
	// Configure graph with proper spacing
	dagreGraph.setGraph({ 
		rankdir: direction,
		nodesep: 80, // Vertical spacing between nodes (reduced from default)
		edgesep: 40, // Horizontal spacing between edges
		ranksep: 120, // Spacing between ranks (reduced from default)
		marginx: 50, // Horizontal margin
		marginy: 50, // Vertical margin
	});

	// Add nodes to the graph with more accurate dimensions
	nodes.forEach(node => {
		// Use actual node dimensions if available, otherwise use reasonable defaults
		const width = node.width || 280;
		const height = node.height || 80;
		dagreGraph.setNode(node.id, { width, height });
	});

	// Add edges to the graph
	edges.forEach(edge => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	// Calculate layout
	dagre.layout(dagreGraph);

	// Apply calculated positions to nodes
	const layoutedNodes = nodes.map(node => {
		const nodeWithPosition = dagreGraph.node(node.id);
		const width = node.width || 280;
		const height = node.height || 80;
		
		return {
			...node,
			position: {
				x: nodeWithPosition.x - (width / 2), // Center offset based on actual width
				y: nodeWithPosition.y - (height / 2), // Center offset based on actual height
			},
		};
	});

	return layoutedNodes;
}

/**
 * Applies auto-layout to a ReactFlow instance
 * @param reactFlowInstance - The ReactFlow instance
 * @param direction - Layout direction
 * @param fitView - Whether to fit view after layout
 */
export function applyAutoLayout(reactFlowInstance: any, direction = 'TB', fitView = true): void {
	if (!reactFlowInstance) {
		console.warn('Cannot apply auto-layout: ReactFlow instance not available');
		return;
	}

	const nodes = reactFlowInstance.getNodes();
	const edges = reactFlowInstance.getEdges();

	if (nodes.length === 0) {
		console.warn('Cannot apply auto-layout: No nodes to layout');
		return;
	}

	const layoutedNodes = getLayoutedElements(nodes, edges, direction);
	reactFlowInstance.setNodes(layoutedNodes);

	if (fitView) {
		// Delay fitView slightly to ensure nodes are repositioned
		setTimeout(() => {
			reactFlowInstance.fitView({ padding: 0.2 });
		}, 50);
	}
}
